import type { StepIconProps } from '@mui/material/StepIcon';
import { styled } from '@mui/material/styles';
import { intent, radius, sizes, surface, text } from '../../theme/tokens';

/**
 * Custom step-icon slot for `StepLabel` — the 36px numbered circle from Figma (node 1578:5746).
 *
 * MUI's default `StepIcon` is a single-colour SVG that (a) can't express a white/subtle fill
 * PLUS a separate border, and (b) swaps the number for a check-mark in the completed state.
 * The Figma spec keeps the NUMBER in every state and needs a fill+border split, so — like the
 * custom RadioIcons — we replace the icon via the sanctioned `slots.stepIcon` API (wired
 * globally in theme/components.ts as `MuiStepLabel.defaultProps.slots.stepIcon`).
 *
 * States (Figma `Step` variants):
 * - Default (upcoming): Surface/White fill, Intent/Neutral border, muted number.
 * - Active (current):   Intent/Primary Subtle fill, Intent/Primary border, primary number.
 * - Done (completed):   Intent/Primary Solid fill, white number.
 * - Error (not in Figma — best-effort): Intent/Error subtle fill + border + number.
 */
type IconState = Pick<StepIconProps, 'active' | 'completed' | 'error'>;

const StyledStepperIconRoot = styled('span', {
  name: 'AppStepperIcon',
  shouldForwardProp: prop => prop !== 'ownerState',
})<{ ownerState: IconState }>(({ theme }) => ({
  // Number typography = the `button` variant (14px / lh 22 / 600 / uppercase — digits unaffected).
  ...theme.typography.button,
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: sizes.height.h36,
  height: sizes.height.h36,
  borderRadius: radius.pill, // clamps to a full circle on the h36 box
  // Default (upcoming) state.
  backgroundColor: surface.white,
  border: `${sizes.border.bd1}px solid ${intent.neutral.border}`,
  color: text.secondary,
  variants: [
    {
      props: ({ ownerState }: { ownerState: IconState }) => !!ownerState.active && !ownerState.error,
      style: {
        backgroundColor: intent.primary.subtle,
        borderColor: intent.primary.border,
        color: intent.primary.text,
      },
    },
    {
      props: ({ ownerState }: { ownerState: IconState }) => !!ownerState.completed && !ownerState.error,
      style: {
        backgroundColor: intent.primary.solid,
        borderColor: intent.primary.solid, // same as fill → reads as no border (Figma "Done")
        color: text.white,
      },
    },
    {
      props: ({ ownerState }: { ownerState: IconState }) => !!ownerState.error,
      style: {
        backgroundColor: intent.error.subtle,
        borderColor: intent.error.border,
        color: intent.error.text,
      },
    },
  ],
}));

export function StepperIcon(props: StepIconProps) {
  const { active = false, completed = false, error = false, icon, className } = props;
  return (
    <StyledStepperIconRoot ownerState={{ active, completed, error }} className={className}>
      {icon}
    </StyledStepperIconRoot>
  );
}
